import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Truck, Camera, MessageCircle, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Gift, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { alertasApi, errorMsg } from './alertasObra.api';
import { estadoForzado, olvidarCombo } from '../comboIA/comboIA';
import ReglasEntregas from './ReglasEntregas';
import EntregasDestinatarios from './EntregasDestinatarios';

/* Alertas de entregas: son parte del combo "IA de obra" (USD 15/mes por cámara, 30 días gratis), que
   también abre Buscador de obra, Entradas y salidas, Materiales y Acopio. Con el modelo nuevo (iaV3) ya
   funciona: al contratarlo se prende el análisis de esa cámara y el aviso «Llegó material a la obra» se
   configura abajo. El primer cobro es a los 30 días (prueba de Stripe). Los que compraron el adicional
   viejo de entregas (USD 20) siguen activos y ven el combo completo. */

const PARAMS = ['ae', 'ae_session'];
const urlLimpia = () => {
  const u = new URL(window.location.href);
  PARAMS.forEach((k) => u.searchParams.delete(k));
  return u.toString();
};
const fecha = (iso) => {
  try { return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' }); } catch (e) { return ''; }
};

const AddonEntregas = ({ feedID, onIrAObra }) => {
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [acepto, setAcepto] = useState(false);
  const confirmado = useRef(false);

  const cargar = useCallback(() => {
    if (!feedID) return;
    setError(null);
    const f = estadoForzado(); // solo local: /__preview-alertas?combo=activo|prueba|no
    if (f) { setEstado(f); return; }
    alertasApi.comboEstado(feedID).then(setEstado).catch((e) => setError(errorMsg(e)));
  }, [feedID]);

  // Vuelta del pago (?ae=ok&ae_session=…) o del pago cancelado (?ae=cancelado).
  useEffect(() => {
    if (!feedID) return;
    const q = new URLSearchParams(window.location.search);
    const limpiar = () => window.history.replaceState(null, '', urlLimpia());
    if (q.get('ae') === 'ok' && q.get('ae_session') && !confirmado.current) {
      confirmado.current = true;
      setOcupado(true);
      alertasApi.entregasConfirmar(feedID, q.get('ae_session'))
        .then((r) => { setEstado(r); olvidarCombo(feedID); toast.success('¡Listo! Empezó tu prueba gratis de IA de obra.'); })
        .catch((e) => { toast.error(errorMsg(e)); cargar(); })
        .finally(() => { setOcupado(false); limpiar(); });
      return;
    }
    if (confirmado.current) return; // ya se está confirmando: no pisar el resultado con un estado viejo
    if (q.get('ae') === 'cancelado') { toast.info('No se completó la contratación. No se cobró nada.'); limpiar(); }
    cargar();
  }, [feedID, cargar]);

  // IA Pro sin tarjeta: con el consentimiento queda 30 días gratis y la obra se empieza a analizar desde ahora.
  const activar = async () => {
    if (!acepto) return;
    setOcupado(true);
    try {
      setEstado(await alertasApi.comboActivar(feedID, 'Alertas de entregas'));
      olvidarCombo(feedID);
      toast.success('¡Listo! IA Pro activa: desde ahora analizamos tu obra con IA.');
    } catch (e) { toast.error(errorMsg(e)); }
    setOcupado(false);
  };
  const portal = async () => {
    setOcupado(true);
    try { window.location.href = (await alertasApi.comboPortal(feedID, urlLimpia())).url; } catch (e) { toast.error(errorMsg(e)); setOcupado(false); }
  };
  const cortesia = async (activo) => {
    setOcupado(true);
    try { setEstado(await alertasApi.entregasCortesia(feedID, activo)); toast.success(activo ? 'Activado sin cargo para este cliente.' : 'Cortesía quitada.'); } catch (e) { toast.error(errorMsg(e)); }
    setOcupado(false);
  };

  const dias = estado?.trialDays || 30;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center shrink-0"><Truck size={18} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-800">Alertas de entregas</h3>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-amber-800 bg-amber-50">En proceso</span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-emerald-800 bg-emerald-50 inline-flex items-center gap-1" data-combo-precio><Sparkles size={11} />PRO · {dias} días gratis</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">Te avisamos por WhatsApp o mail cuando llega un camión, un mixer o material a la obra, con la foto de antes y después.</p>
          <p className="text-xs text-gray-500 mt-1">Viene en <b>IA Pro</b>, que también incluye Buscador de obra, Entradas y salidas, Materiales y Acopio. {dias} días gratis, sin tarjeta.</p>
        </div>
      </div>

      <p className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-entregas-en-proceso>
        🚧 Las alertas de entregas todavía están en proceso: la IA puede equivocarse o no ver una entrega. Las estamos ajustando con tus 👍/👎.
      </p>

      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[[Truck, 'Camiones, mixers y materiales'], [Camera, 'Foto de antes y después'], [MessageCircle, 'Por WhatsApp o mail']].map(([Icon, t]) => (
          <li key={t} className="flex items-center gap-2 text-xs text-gray-700 bg-gray-50 rounded-lg px-3 py-2"><Icon size={14} className="text-gray-500 shrink-0" />{t}</li>
        ))}
      </ul>

      <div className="mt-4">
        {error ? (
          <div className="text-sm text-gray-700 bg-red-50 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <span>No pudimos ver el estado: {error}</span>
            <button type="button" onClick={cargar} className="text-sm font-semibold text-red-700 hover:underline">Reintentar</button>
          </div>
        ) : !estado ? (
          <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" />{ocupado ? 'Confirmando tu pedido…' : 'Cargando…'}</div>
        ) : estado.pagoPendiente ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-2">
            <p className="text-sm font-semibold text-amber-900 inline-flex items-center gap-1.5"><AlertTriangle size={16} />Hay un problema con el pago</p>
            <p className="text-xs text-amber-900">Revisá la tarjeta para que no se interrumpa la activación.</p>
            <button type="button" disabled={ocupado} onClick={portal} className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 hover:underline disabled:opacity-50"><ExternalLink size={14} />Revisar el pago</button>
          </div>
        ) : estado.contratado ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 space-y-1.5">
            <p className="text-sm font-semibold text-emerald-800 inline-flex items-center gap-1.5"><CheckCircle2 size={16} />{estado.feedIncluido ? (estado.estado === 'prueba' ? `IA Pro en prueba${estado.diasRestantes != null ? `: te quedan ${estado.diasRestantes} días` : ''}. Elegí abajo a quién le avisamos y de qué` : 'IA Pro activa en esta cámara: elegí abajo a quién le avisamos y de qué') : 'Pedido recibido: lo estamos activando para tu obra'}</p>
            <p className="text-xs text-emerald-900">
              {estado.cortesia
                ? `Activado sin cargo por Lisual.${estado.feedIncluido ? '' : ' Te avisamos cuando empiece a funcionar.'}`
                : `${estado.feedIncluido ? 'La IA ya revisa esta cámara cada 10 minutos.' : 'Te avisamos cuando empiece a funcionar.'} ${estado.trialEnd ? (estado.puedePortal ? `El primer cobro es recién el ${fecha(estado.trialEnd)}; podés cancelar antes sin costo.` : `La prueba gratis termina el ${fecha(estado.trialEnd)}.`) : ''}`}
            </p>
            {!estado.feedIncluido && <p className="text-xs text-emerald-900">Tu empresa lo pidió para otra cámara. Si también lo querés en esta, escribinos y la sumamos.</p>}
            {!estado.cortesia && estado.puedePortal && (
              <button type="button" disabled={ocupado} onClick={portal} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:underline disabled:opacity-50"><ExternalLink size={14} />Ver facturación o cancelar</button>
            )}
          </div>
        ) : (
          estado.pro?.pruebaUsada ? (
          <p className="text-xs text-gray-700 bg-gray-50 rounded-xl px-4 py-3" data-combo-usada>Esta cámara ya usó sus {dias} días gratis de IA Pro. Escribinos y lo reactivamos.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-700 bg-blue-50 rounded-xl px-4 py-3">
              <span className="font-semibold">{dias} días gratis, sin tarjeta.</span> Incluye las alertas de entregas y las 4 pantallas de Inteligencia: Buscador de obra, Entradas y salidas, Materiales y Acopio.
              {' '}<b>Tu obra se empieza a analizar con IA recién cuando lo activás.</b>
            </p>
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} className="mt-0.5 h-4 w-4" data-combo-acepto />
              <span>{estado.pro?.consentimiento || 'Acepto que Lisual analice con inteligencia artificial las fotos de esta cámara a partir de este momento, durante 30 días gratis y sin tarjeta.'}</span>
            </label>
            <button type="button" disabled={ocupado || !acepto} onClick={activar} className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50" data-combo-activar>
              {ocupado ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}Activar IA Pro gratis
            </button>
          </div>
        )
        )}
      </div>

      {estado && ((estado.contratado && estado.feedIncluido) || estado.admin) && (
        <>
          <EntregasDestinatarios feedID={feedID} onIrAObra={onIrAObra} />
          <ReglasEntregas feedID={feedID} />
        </>
      )}

      {estado?.admin && (estado.cortesia || !estado.contratado) && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap text-xs text-gray-500">
          <Gift size={14} />Solo Lisual:
          {estado.cortesia
            ? <button type="button" disabled={ocupado} onClick={() => cortesia(false)} className="font-semibold text-gray-700 hover:underline">Quitar la cortesía</button>
            : !estado.contratado && <button type="button" disabled={ocupado} onClick={() => cortesia(true)} className="font-semibold text-blue-700 hover:underline">Activar sin cargo</button>}
        </div>
      )}
    </div>
  );
};

export default AddonEntregas;
